<template>
  <div
    v-if="productmeterNetworks.length"
    class="woContainers"
  >
    <VBoxWithTitle
      v-for="net in productmeterNetworks"
      :key="net.ref"
      containerClass="networkBox"
      :customClass="boxIsOpen ? 'topRadius' : 'networkBoxTitle'"
      closingIcon="mdi-chevron-up"
      collapsible
      openingIcon="mdi-chevron-down"
      :openedBox="net"
      class="productLevelBox"
    >
      <template #header>
        <v-toolbar-title>
          {{ net.name }}
        </v-toolbar-title>
      </template>
      <template #content>
        <VTableRemote
          ref="tableRemoteRef"
          :isSearchable="false"
          :showHeader="false"
          :items="net.meters"
          :headers="tableHeaders"
          :centerColumns="['dataSource', 'quantityAdded', 'value', 'measureUnit', 'refresh', 'graphOnReportPdf']"
          :pagination="{}"
          :hideDefaultFooter="true"
          sortBy="treeOrder"
          class="blockTable"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td class="text-start w-name">
                {{ item.productName }}
              </td>
              <td class="text-start w-lname">
                {{ item.component.name }}
              </td>
              <td class="text-center w-sname">
                <div class="d-flex flex-column dataSource justify-center">
                  <img
                    class="dataSourceImg"
                    :src="`/assets/${dataSourceIcon[item.dataSource]}`"
                    :alt="item.dataSource"
                  >
                  <div class="dataSourceTitle">
                    {{ item.dataSource }}
                  </div>
                </div>
              </td>
              <td class="text-center pa-0">
                <div class="woSection w-value">
                  <Popover
                    :emptyTrigger="!item.value"
                    top="60"
                  >
                    <template v-slot:trigger="{ onClick }">
                      <div
                        class="woSectionData"
                      >
                        <div
                          class="woValueSection w-value"
                        >
                          <v-icon
                            v-if="showAlertIcon(item)"
                            size="20"
                            color="red"
                            @click="()=> openPopover(onClick, item)"
                          >
                            mdi-alert-outline
                          </v-icon>
                          <div
                            v-if="item.measureId"
                            style="display: flex; flex-direction: row;"
                          >
                            <input
                              v-model="item.value"
                              type="number"
                              :class="{redAlert: showAlertIcon(item), woValue: true, manualInputStyle: true}"
                              :style="!item.value ? 'width: max-content' : ''"
                              :disabled="isCompletedOrLocked"
                              @blur="manualInput(item, 'value')"
                            >
                            <v-btn
                              v-if="item.value"
                              icon
                              color="primary"
                              xSmall
                              :disabled="isCompletedOrLocked"
                              @click="openCommentModal(item)"
                            >
                              <v-icon>
                                {{ item.measureNote ? 'chat_bubble' : 'chat_bubble_outline' }}
                              </v-icon>
                            </v-btn>
                          </div>

                          <div
                            v-else
                            :class="{redAlert: showAlertIcon(item), woValue: true}"
                          >
                            {{ item.value }}
                          </div>
                        </div>
                        <div class="woBar" />
                        <div
                          class="woDate"
                        >
                          {{ item.lastReadingDate }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:content="{ isClosed, close }">
                      <FlowMetersAlert
                        :isClosed="isClosed"
                        :meter="item"
                        :close="close"
                        :refreshReading="(manualChanges)=> refreshReading(item, manualChanges)"
                      />
                    </template>
                  </Popover>
                </div>
              </td>
              <td class="text-center w-value">
                <div
                  v-if="item.dataSource === SOURCE_MANUAL"
                  class="woSection w-m-center"
                >
                  <input
                    v-model="item.quantityAdded"
                    type="number"
                    class="woValue manualInputStyle"
                    :disabled="isCompletedOrLocked || !item.value"
                    @blur="manualInput(item, 'quantityAdded')"
                  >
                  <div class="woBar" />
                </div>
              </td>
              <td class="text-center pa-0 w-icon">
                <div class="woSection">
                  <Tooltip>
                    <template #trigger>
                      <v-icon
                        v-if="item.dataSource !== SOURCE_MANUAL"
                        class="woReadingState"
                        :disabled="isCompletedOrLocked"
                        @click="()=> refreshReading(item)"
                      >
                        mdi-refresh
                      </v-icon>
                    </template>
                    <template #content>
                      <div v-if="item.value">
                        {{ $t('devices.latestValueReading') }}
                      </div>
                      <div v-else>
                        {{ $t('devices.noValueReading') }}
                      </div>
                      <div v-if="item.value">
                        {{ item.lastReadingDate }}
                      </div>
                    </template>
                  </Tooltip>
                </div>
              </td>
              <td class="text-center w-name">
                {{ item.measureUnit ? $t(`devices.productUnitMeasure.${item.measureUnit.toLowerCase()}`) : '' }}
              </td>
              <td class="text-center w-name">
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-icon
                      v-if="item.isPartOfMassBalance"
                      v-bind="attrs"
                      class="hover-icon"
                      v-on="on"
                    >
                      mdi-format-list-bulleted-square
                    </v-icon>
                  </template>
                  <div class="custom-tooltip">
                    <v-icon
                      style="color: white; padding-right: 25px"
                    >
                      mdi-information
                    </v-icon>
                    <div style="display: flex; flex-direction: column; justify-content: center; margin-top: 15px">
                      <p>{{ $t('dashboard.e4.itemRequired') }}</p>
                    </div>
                  </div>
                </v-tooltip>
              </td>
              <td class="text-start w-name text--disabled underlined">
                {{ item.dailyVolumeConsumption }}
              </td>
              <td class="text-center w-name">
                <VCheckbox
                  disabled
                  :readonly="true"
                  :value="item.graphOnReportPdf"
                  class="d-flex justify-center"
                />
              </td>
            </tr>
          </template>
        </VTableRemote>
      </template>
    </VBoxWithTitle>
  </div>
  <div
    v-else
    class="title emptyWoContainers"
  >
    {{ $t('devices.emptyProductLevel') }}
  </div>
</template>

<script>
import VBoxWithTitle from '@/components/shared/containers/VBoxWithTitle.vue';
import { mapGetters } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import { SOURCE_MANUAL } from '@/aquassist-shared/constants/types/flowMeters';
import Popover from '@/components/popover/Popover.vue';
import Tooltip from '@/components/tooltip/Tooltip.vue';
import FlowMetersAlert from '@/views/workOrders/flowMeters/FlowMetersAlert/FlowMetersAlert.vue';
import moment from 'moment/moment';
import completionStatus from '@/constants/workOrderStatusChoices';
import AddAutomaticSentenceForm from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm.vue';
import Vue from 'vue';

export default {
    name: 'WorkOrderProductLevel',
    components: {
        FlowMetersAlert,
        Tooltip,
        Popover,
        VCheckbox,
        VTableRemote,
        VBoxWithTitle,
    },
    props: {
        productmeterNetworks: {
            type: Array,
            default: () => [],
        },
        tables: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            boxIsOpen: false,
            activityId: null,
        };
    },
    computed: {
        ...mapGetters(['site', 'workOrder']),
        SOURCE_MANUAL() {
            return SOURCE_MANUAL;
        },
        tableHeaders() {
            return [
                'productName',
                'component',
                'dataSource',
                'value',
                'quantityAdded',
                'refresh',
                'measureUnit',
                'massBalanceE4',
                'dailyVolumeConsumption',
                'graphOnReportPdf',
            ].map(header => ({
                text: header === 'refresh' ? '' : this.$t(`devices.${header}`),
                value: header,
                sortable: false,
                class: this.getClassName(header),
            }));
        },
        dataSourceIcon() {
            return {
                MANUAL: 'manual_flow_meter.png',
                IOT: 'iot_flow_meter.png',
                CONTROLLER: 'controller_flow_meter.png',
            };
        },
        isCompletedOrLocked() {
            return this.workOrder.completionStatus === completionStatus.COMPLETED || !!this.workOrder?.lockData?.isLocked;
        },
    },
    created() {
        this.activityId = this.workOrder.activities?.measuresAndSamplesCollect?.id;
    },
    methods: {
        getClassName(header) {
            let className = 'text-no-wrap';
            if (header === 'value') {
                className += ' d-width';
            }
            if (['dailyVolumeConsumption', 'graphOnReportPdf'].includes(header)) {
                className += ' text--disabled';
            }
            return className;
        },
        async addMeasure(activityId, productLevel, analysis, manualChanges) {
            const payload = {
                team: this.workOrder.team.ref,
                unit: productLevel.measureUnit,
                id: analysis,
            };
            if (manualChanges.value) {
                payload.value = manualChanges ? manualChanges.value : productLevel.measureValue.toString();
                payload.measuredAt = manualChanges ? manualChanges.lastReadingDate : productLevel.lastReadingDate;
            }
            if (manualChanges.quantityAdded) {
                payload.quantityAdded = manualChanges ? manualChanges.quantityAdded : productLevel.quantityAdded;
            }
            await this.$rq.addMeasureToAnalysis(this.workOrder.id, activityId, [payload]);
        },
        async refreshReading(productLevel, manualChanges) {
            if (!manualChanges) {
                const payload = {
                    activityId: this.activityId,
                    workOrder: {
                        id: this.workOrder.id,
                        site: this.workOrder.site.ref,
                    },
                    analysisTypeId: productLevel.analysisType,
                    componentId: productLevel.component.id,
                    analysisConfigId: productLevel.analysisConfigId,
                };
                const { data } = await this.$rq.fetchLastMeasure(payload);

                const newMeasure = {
                    team: this.workOrder.team.ref,
                    unit: productLevel.measureUnit,
                    id: productLevel.analysisId,
                    value: data.value,
                    measuredAt: data.lastmodified,
                };
                const reponse = await this.$rq.addMeasureToAnalysis(this.workOrder.id, this.activityId, [newMeasure]);
                if (reponse.status === 200) {
                    this.showMessage('success');
                    await this.getMetersByNetwork();
                } else {
                    this.showMessage('error');
                }
            } else if (manualChanges) {
                await this.addMeasure(this.activityId, productLevel, productLevel.analysisId, manualChanges);
            }
        },
        getAlertType(newValue, oldValue) {
            if (Number(newValue) > Number(10 * oldValue)) {
                return 'UPPER';
            }
            if (Number(newValue) < Number(oldValue)) {
                return 'LOWER';
            }
            return '';
        },
        async manualInput(item, key) {
            const isValue = key === 'value' || key === 'quantityAdded';
            const manualChanges = {
                [key]: item[key] || (isValue ? '' : 0),
            };
            if (isValue) {
                manualChanges.lastReadingDate = moment().toISOString();
            }
            await this.refreshReading(item, manualChanges);
            // En saisissant la donnée on créé une measure. Mais celle cie n'est pas renvoyée.
            // Nous devons la renvoyer pour pouvoir saisie un commentaire
            // (car le commentaire a besoin du measureId)
            if (!item.measureId) {
                this.getMetersByNetwork();
            }
        },
        openPopover(popoverClick, meter) {
            if (meter.dataSource === SOURCE_MANUAL) popoverClick();
        },
        showAlertIcon() {
            // TODO: Validate the rule in next story(AQ-8429)
            // meter.dataSource === SOURCE_MANUAL && meter.alertType !== '';
            return false;
        },
        async openCommentModal(item) {
            const payload = {
                note: item.measureNote || null,
            };

            this.$root.$emit('showModal', {
                title: this.$t('visitSummary.addAutomaticSentence'),
                component: Vue.component('addAutomaticSentenceForm', AddAutomaticSentenceForm),
                payload: { measure: payload || null, isAnalysis: true },
                onConfirm: async requestPayload => {
                    const reponse = await this.$rq.updateMeasureNote(
                        this.activityId,
                        this.tables[0].id,
                        item.measureId,
                        requestPayload,
                    );
                    if (reponse.status === 200) this.showMessage('success');
                    else this.showMessage('error');
                    this.getMetersByNetwork();
                },
            });
        },
        getIconName(networkId, componentId) {
            return this.hasComment(networkId, componentId) ? 'chat_bubble' : 'chat_bubble_outline';
        },
        hasComment(networkId, componentId) {
            return !!this.getComment(networkId, componentId);
        },
        getComment(networkId, componentId) {
            // Trouver le bon network
            const goodNetwork = this.tables[0].networks.find(n => n.id === networkId);
            if (!goodNetwork) return null;

            let component;
            // eslint-disable-next-line no-restricted-syntax
            for (const componentGroup of goodNetwork.components) {
                component = componentGroup.find(c => c.id === componentId);
                if (component) break;
            }
            if (!component) return null;

            return component;
        },
        showMessage(type) {
            if (type === 'success') {
                this.$toasted.global.toast_success({
                    message: this.$t('msg.tableUpdated'),
                });
            }
            if (type === 'error') {
                this.$toasted.global.toast_error({
                    message: this.$t('msg.error'),
                });
            }
        },
        getMetersByNetwork() {
            this.$emit('getMetersByNetwork');
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./../flowMeters/WorkOrderFlowMetersStyles.scss";


.productLevelBox {
  .v-data-table__wrapper {
    overflow-y: hidden;
  }
}

.hover-icon {
  border: 1px solid black;
  border-radius: 4px;
  text-align: center;
  transition: color 0.3s;
}

.hover-icon:hover {
  color: white;
  background-color: #26384B;
}

  .custom-tooltip {
    background-color: #26384B;
    color: white;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    height: 60px;
  }

.v-tooltip__content {
  background-color: #26384B;
}

</style>
